import React from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { ChevronDown, Code2, FileText, Globe, Image as ImageIcon, PenLine, Sparkles } from 'lucide-react';
import { SectionIntro } from './SectionIntro';
import { BrandLogo } from './AppChrome';

/**
 * Speed and control, argued as ONE mosaic rather than two stacked lists.
 *
 * The previous treatment was two identical heading + row-list blocks, which
 * made "Go Faster" and "Stay In Control" read as two unrelated features. They
 * are the same decision — that is the page's whole thesis — so they share one
 * board here, with each panel tagged by which side of the promise it serves
 * and sized by how much it matters.
 *
 * Every visual is drawn in markup: no images, no icon set, no chart library.
 */

const EASE = [0.16, 1, 0.3, 1] as const;

/** One panel of the board. Order in the DOM is the reading order. */
function Panel({
  children,
  className = '',
  tint = 'white',
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  tint?: 'white' | 'peach' | 'sand';
  delay?: number;
}) {
  const reduce = useReducedMotion();
  const surface = tint === 'peach' ? 'bg-[#FFF4EC]' : tint === 'sand' ? 'bg-[#F7F2EA]' : 'bg-white';
  // THE SCALE IS GONE. At 0.99 it was imperceptible as movement, and scaling a
  // hairline-bordered card mid-transition softens its own border and shadow —
  // so the only thing it contributed was a slightly blurry edge on the way in.
  // Rise alone, further (18px) and faster (300ms), reads as deliberate.
  return <motion.article initial={reduce ? false : {
    opacity: 0,
    y: 18
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-90px'
  }} transition={{
    duration: 0.3,
    delay,
    ease: EASE
  }} className={`relative flex flex-col overflow-hidden rounded-[22px] border border-[#E7E0D4] ${surface} shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)] ${className}`} data-id="element-335" data-mp="115kaedafmahm">
      {children}
    </motion.article>;
}
function PanelCopy({
  title,
  copy,
  className = ''
}: {
  title: string;
  copy: string;
  className?: string;
}) {
  return <div className={className} data-id="element-336" data-mp="1pmhveivargco">
      <h3 className="font-title text-[22px] font-bold leading-[1.15] tracking-[-0.02em] text-[#2B211A]" data-id="element-337" data-mp="1koq33nqqps7y">
        {title}
      </h3>
      <p className="mt-2.5 max-w-[42ch] text-[14.5px] leading-relaxed text-[#6B5D52]" data-id="element-338" data-mp="1fpda0l99zowx">
        {copy}
      </p>
    </div>;
}

/**
 * The four kinds of work the copy hands over, shown being handed over. The
 * previous visual was two generic app windows — it said "software happened"
 * without saying what, which is why it read as decoration next to copy that
 * names four specific jobs.
 */
const HANDOFF = [{
  label: 'Research',
  detail: 'Pulled 14 sources on the Q3 market',
  state: 'done'
}, {
  label: 'Formatting',
  detail: 'Rebuilt the deck to the brand template',
  state: 'done'
}, {
  label: 'Follow-up',
  detail: 'Chased 6 open threads, 4 replied',
  state: 'done'
}, {
  label: 'First draft',
  detail: 'Board one-pager, variance notes included',
  state: 'active'
}] as const;

/**
 * The question-and-answer the numbers panel is actually about: something asked
 * in plain language, answered from the systems themselves. The bar chart that
 * used to sit here counted hours saved — a different claim entirely from the
 * one the copy makes.
 */
const METRIC_SOURCES = ['hubspot', 'salesforce', 'gmail'] as const;

/**
 * ASKED WHERE THE WORK ALREADY HAPPENS. A single figure in a card said the
 * number exists; a thread says anyone can just ask for it, in the channel they
 * are already in. The earlier exchange is faded and cropped at the top so the
 * panel reads as a slice of an ongoing conversation rather than a two-message
 * demo — the claim is "any question", not "this question".
 */
function AskThread() {
  return <div className="mt-6 space-y-3" data-id="element-339" data-mp="3cl29r924neh9">
      {/* TWO SIDES, NOT TWO STACKED CARDS. Both bubbles used to run the full
       width of the panel with only their corner notch to tell them apart, so
       at phone width the exchange read as two paragraphs in boxes rather than
       as a conversation. The question is now an OUTGOING message — pulled to
       the right, capped short of the panel's width, with the asker's own
       avatar beside it — and the answer stays incoming on the left under
       Giga's mark. Sides and a ragged right edge are what make a thread read
       as a thread at any size. */}
      <div className="flex items-start justify-end gap-2" data-id="element-340" data-mp="1wtzy26yeizq3">
        <p className="max-w-[86%] rounded-2xl rounded-br-md bg-white px-3.5 py-2.5 text-[13px] leading-snug text-[#2B211A] shadow-[0_1px_2px_rgba(43,33,26,0.04),0_6px_16px_-6px_rgba(43,33,26,0.16)]" data-id="element-341" data-mp="1wtgj8z41srga">
          <span className="font-bold text-[#C2410C]" data-id="element-342" data-mp="25v7ok2uoew9m">@Giga</span> where did last
          month's new customers come from?
        </p>
        {/* Initials, not a stock face. A photographic avatar in a two-message
         demo invites the reader to wonder who the person is; initials read as
         "someone on your team" and stay out of the way. */}
        <span aria-hidden="true" className="mt-0.5 flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[#E6DDD0] text-[9.5px] font-bold text-[#6B5D52]" data-id="element-343" data-mp="2s338zlls10k3">
          DH
        </span>
      </div>
      <div className="flex items-start gap-2" data-id="element-344" data-mp="2ibywcsjkprsf">
        <GigaMark data-id="element-345" data-mp="2ianr0oahcbz7" />
        <div className="min-w-0 max-w-[92%] rounded-2xl rounded-bl-md bg-white p-3.5 shadow-[0_1px_2px_rgba(43,33,26,0.04),0_6px_16px_-6px_rgba(43,33,26,0.16)]" data-id="element-346" data-mp="24dinow25hr7n">
          <p className="text-[13px] leading-snug text-[#2B211A]" data-id="element-347" data-mp="29dn4kn6z0nye">
            <span className="font-bold" data-id="element-348" data-mp="1ug4khy58myxa">61% from outbound</span>, referrals
            doubled — and cost per new customer is{' '}
            <span className="font-bold text-[#C2410C]" data-id="element-349" data-mp="1zeee44bbdrgs">down 18%</span>.
          </p>
          {/* Tightened so all three sources hold ONE row inside a phone-width
           bubble — wrapped, the last chip sat alone on its own line and the
           citation read as a list of features rather than as a footnote. */}
          <div className="mt-2.5 flex flex-wrap gap-1" data-id="element-350" data-mp="3s8nkwv1g9a8l">
            {METRIC_SOURCES.map(slug => <span key={slug} className="inline-flex items-center gap-1 rounded-full border border-[#EFEAE1] bg-[#FCFAF6] px-1.5 py-[3px]" data-id="element-351" data-mp="3ne031f0ybe2z">
                <BrandLogo slug={slug} className="size-[11px]" data-id="element-352" data-mp="3ebqrclty5eo0" />
                <span className="text-[10px] font-semibold text-[#6B5D52]" data-id="element-353" data-mp="3edlfe90ilmwb">
                  {SOURCE_NAMES[slug]}
                </span>
              </span>)}
          </div>
        </div>
      </div>
    </div>;
}
const SOURCE_NAMES: Record<string, string> = {
  hubspot: 'HubSpot',
  salesforce: 'Salesforce',
  gmail: 'Gmail'
};
/** Giga's own mark, not a generic sparkle standing in for it. */
function GigaMark() {
  return <span aria-hidden="true" className="mt-0.5 flex size-[22px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#EA580C]" data-id="element-354" data-mp="xmczhs92w59y">
      <BrandLogo slug="giga" onDark mono className="size-[13px]" data-id="element-355" data-mp="sv3dr2qnhm3c" />
    </span>;
}

/**
 * The meetings that stop happening. "Kill the status meeting" is a claim about
 * something being REMOVED, so the visual has to show removal — a recap card
 * showed the replacement and left the reader to infer the deletion.
 *
 * Each row is struck through and stamped. The tilt is per-row so the stamps
 * read as pressed by hand rather than laid out on a grid.
 */
/**
 * Shared height for the copy block of the two panels in the board's top row.
 * Their titles and leads wrap to different line counts, so without a common
 * floor the two visuals beneath them start at different heights and the row
 * reads as misaligned even though both panels are the same height.
 */
const ROW_COPY = 'md:min-h-[118px]';

/**
 * The same treatment for the board's bottom row — "Built for teams" beside
 * "Model agnostic". Their leads wrap to two and three lines respectively, so
 * without a shared floor the group illustration and the provider list begin at
 * different heights and the row reads as misaligned.
 */
const ROW_COPY_BOTTOM = 'md:min-h-[104px]';
const MEETINGS = [{
  name: 'Daily standup',
  cadence: 'Every day · 15 min',
  tilt: -1.6
}, {
  name: 'Weekly status sync',
  cadence: 'Mondays · 30 min',
  tilt: -3
}, {
  name: 'Ops check-in',
  cadence: 'Thursdays · 30 min',
  tilt: -1
}] as const;
function KilledMeetings() {
  const reduce = useReducedMotion();
  return <div className="mt-6 flex flex-1 flex-col" data-id="element-356" data-mp="2acwv4frv54nx">
      <ul className="space-y-2.5" data-id="element-357" data-mp="2adocalkpqzuo">
      {MEETINGS.map((meeting, i) => <li key={meeting.name} className="flex items-center gap-2.5 rounded-2xl border border-[#EFEAE1] bg-white px-3 py-3 shadow-[0_1px_2px_rgba(43,33,26,0.03)] sm:gap-3 sm:px-3.5 sm:py-3.5" data-id="element-358" data-mp="25f4t2sxi8260" data-mpi={i}>
          <svg aria-hidden="true" viewBox="0 0 24 24" className="size-[18px] shrink-0 text-[#CFC4B7] sm:size-[22px]" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" data-id="element-359" data-mp="2eerrr6yumtvd">
            <rect x="3" y="5.5" width="18" height="15" rx="2.5" data-id="element-360" data-mp="1modi5mxvc3pi" />
            <path d="M7.5 3v3M16.5 3v3M3 10h18" data-id="element-361" data-mp="1mm143kv8edhq" />
          </svg>
          {/* THE STAMP STAYS ON THE RIGHT AT EVERY WIDTH. It used to drop
           below the name on phones because at full size it left the title
           about 90px to wrap into — and a title on two lines cannot be struck
           through correctly. The fix is to make the row SMALLER rather than
           taller: a shorter stamp ("Recapped", since the panel is already
           Giga's) at tighter type and padding leaves the title enough width
           to hold one line, so all three rows stay single-height. */}
          <span className="flex min-w-0 flex-1 items-center justify-between gap-2 sm:gap-3" data-id="element-362" data-mp="2075bhmkxuft0">
            <span className="min-w-0" data-id="element-363" data-mp="1lebicycqu15v">
            <span className="relative inline-block" data-id="element-364" data-mp="1qashsope81y9">
              <span className="block text-[13.5px] font-bold leading-tight text-[#8C7C6E] sm:text-[14.5px]" data-id="element-365" data-mp="1q9h704xj5txn">
                {meeting.name}
              </span>
              {/* The strike DRAWS itself, left to right — a line already
               present is a style, a line being drawn is a decision. */}
              <motion.span aria-hidden="true" className="absolute left-[-2px] right-[-2px] top-[52%] h-[2px] origin-left rounded-full bg-[#EA580C]" initial={reduce ? false : {
                scaleX: 0
              }} whileInView={{
                scaleX: 1
              }} viewport={{
                once: true,
                margin: '-80px'
              }} transition={{
                duration: 0.4,
                delay: 0.15 + i * 0.12,
                ease: EASE
              }} data-id="element-366" data-mp="15pyc6ko8rm3m" />
            </span>
            <span className="mt-0.5 block truncate text-[11.5px] leading-snug text-[#A99A8C] sm:mt-1 sm:text-[12.5px]" data-id="element-367" data-mp="ry1qozh2wag7">
              {meeting.cadence}
            </span>
            </span>
          {/* The stamp lands after the strike: struck first, then replaced. */}
          <motion.span className="w-fit shrink-0 rounded-[5px] bg-[#F9A66C] px-2 py-1 sm:px-2.5 sm:py-1.5" style={{
            rotate: meeting.tilt
          }} initial={reduce ? false : {
            opacity: 0,
            scale: 1.14
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.22,
            delay: 0.42 + i * 0.12,
            ease: 'easeOut'
          }} data-id="element-368" data-mp="27cz6mthjt3bi">
            <span className="flex items-center gap-1 whitespace-nowrap text-[9px] font-bold uppercase tracking-[0.06em] text-[#2B211A] sm:gap-1.5 sm:text-[10px] sm:tracking-[0.08em]" data-id="element-369" data-mp="agl3uxdhsbtw">
              <svg aria-hidden="true" viewBox="0 0 24 24" className="size-[9px] sm:size-[10px]" fill="none" stroke="currentColor" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" data-id="element-370" data-mp="jhj8nhvr8ndj">
                <path d="M4 12.5 9.5 18 20 6.5" data-id="element-371" data-mp="tcune075xula" />
              </svg>
              Recapped<span className="hidden sm:inline" data-id="element-372" data-mp="ofuj9utvj1wt">&nbsp;by Giga</span>
            </span>
          </motion.span>
          </span>
        </li>)}
      </ul>
      {/* THE PAYOFF, in the units the rows are priced in. The three cadences
       above state what was removed; this states what came back — which is the
       thing the reader actually cares about, and it is plain arithmetic from
       the rows themselves rather than a claim they have to take on faith. */}
      <p className="mt-5 flex items-baseline gap-2 border-t border-[#EFEAE1] pt-4 text-[13px] text-[#8C7C6E]" data-id="element-373" data-mp="133poszfkmmz8">
        <span className="font-title text-[19px] font-bold leading-none tracking-[-0.02em] text-[#2B211A]" data-id="element-374" data-mp="182srwebzqj4k">
          2h 15m
        </span>
        back every week, per person
      </p>
    </div>;
}

/**
 * THE AUDIT TRAIL, NOT A FEED.
 *
 * "See it as it happens" was illustrated with three anonymous status lines,
 * which showed that something was logged without showing what a log is FOR.
 * Oversight is answering four questions: who did it, in which system, what
 * they touched, and whether anything was written. Each row now carries all
 * four — and one row is a WRITE, flagged, because a log where nothing is ever
 * consequential proves nothing.
 */
type LogEntry = {
  who: string;
  tool: string;
  action: string;
  scope: 'read' | 'write';
  time: string;
};
const LOG_POOL: readonly LogEntry[] = [{
  who: 'Maya R.',
  tool: 'slack',
  action: 'Summarised #launch',
  scope: 'read',
  time: '14:02'
}, {
  who: 'Dev H.',
  tool: 'salesforce',
  action: 'Updated 12 opportunities',
  scope: 'write',
  time: '13:47'
}, {
  who: 'Finance group',
  tool: 'quickbooks',
  action: 'Pulled the Q3 ledger',
  scope: 'read',
  time: '13:31'
}, {
  who: 'Priya N.',
  tool: 'hubspot',
  action: 'Enriched 40 new leads',
  scope: 'write',
  time: '13:24'
}, {
  who: 'Support group',
  tool: 'zendesk',
  action: 'Tagged 8 escalations',
  scope: 'write',
  time: '13:18'
}, {
  who: 'Tom A.',
  tool: 'notion',
  action: 'Read the launch brief',
  scope: 'read',
  time: '13:05'
}, {
  who: 'Engineering group',
  tool: 'github',
  action: 'Reviewed 3 open PRs',
  scope: 'read',
  time: '12:52'
}] as const;

/**
 * THE LOG, RUNNING.
 *
 * The claim is "as it happens", and a fixed list of three rows argues the
 * opposite: it shows that something WAS logged. Entries now arrive at the top
 * and push the older ones down, oldest falling off the end — so the reader
 * watches the trail being written rather than reading a finished one.
 *
 * Restraint that matters: a new row every ~2.6s, three visible, and the arrival
 * is a short slide with the row's ground flashing warm once before settling. A
 * feed that churns faster than you can read it stops being evidence of
 * oversight and becomes decoration. Height is fixed from the first frame so
 * nothing in the card moves as rows come and go.
 */
function LiveActivity() {
  const reduce = useReducedMotion();
  const [cursor, setCursor] = React.useState(0);
  React.useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setCursor(c => c + 1), 2600);
    return () => window.clearInterval(id);
  }, [reduce]);
  const rows = [0, 1, 2].map(i => {
    const idx = (cursor + i) % LOG_POOL.length;
    return {
      ...LOG_POOL[idx],
      key: `${cursor + i}`
    };
  });
  // Follows the copy directly. Bottom-anchored (mt-auto) it dropped to the
  // foot of whatever the tallest panel in the row happened to be, leaving a
  // void between the paragraph and the feed it describes.
  return <div className="mt-6" data-id="element-375" data-mp="1rvhdkf14vtt4">
      <p className="mb-2 flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#A99A8C]" data-id="element-376" data-mp="1rw8y8ut7074m">
        {/* Two rings: a solid core with a halo expanding out of it, rather
         than a dot fading in and out. A pulse that leaves the source reads as
         something being emitted; opacity alone reads as a dimmer. */}
        <span aria-hidden="true" className="relative flex size-[6px] shrink-0" data-id="element-377" data-mp="1e1q416nek9xe">
          {!reduce && <span className="absolute inset-0 animate-ping rounded-full bg-[#16A34A] opacity-75" data-id="element-378" data-mp="193evr0cra7ha" />}
          <span className="relative size-[6px] rounded-full bg-[#16A34A]" data-id="element-379" data-mp="2ph51scnfr5tk" />
        </span>
        Activity · live
      </p>
      {/* Fixed height for exactly three rows, so arrivals never resize the
       card, and clipped so the departing row leaves rather than shrinks. */}
      <div className="relative overflow-hidden rounded-xl border border-[#EFEAE1] bg-[#FCFAF6]" style={{
      height: 3 * 51
    }} data-id="element-380" data-mp="2wkuy8714jqao">
        <AnimatePresence initial={false} data-id="element-381" data-mp="31jeoukqnxq9d">
          {rows.map((entry, i) => <motion.div key={entry.key} layout={!reduce} initial={reduce ? false : {
          opacity: 0,
          y: -18
        }} animate={{
          opacity: i === 2 ? 0.55 : 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.34,
          ease: EASE
        }} className={`flex items-center gap-2.5 px-3 ${i > 0 ? 'border-t border-[#EFEAE1]' : ''}`} style={{
          height: 51
        }} data-id="element-382" data-mp="36jtzh4jk8jnn" data-mpi={i}>
              <span className="flex size-[26px] shrink-0 items-center justify-center rounded-lg border border-[#F1ECE3] bg-white" data-id="element-383" data-mp="1q6c55gt9okkz">
                {/* Full colour: these are three named systems, and the point
                 of the row is which one was touched — the flattening rule
                 exists for the 28-mark directory, where unrelated brand
                 colour reads as a collage. Three marks is not a collage. */}
                <BrandLogo slug={entry.tool} className="size-[13px]" data-id="element-384" data-mp="382zcpf6annz" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-385" data-mp="3uem9s3h3kblr">
                <span className="block truncate text-[12.5px] font-semibold leading-tight text-[#2B211A]" data-id="element-386" data-mp="3ui1nb1v4hcl2">
                  {entry.action}
                </span>
                <span className="mt-0.5 block truncate text-[11px] leading-snug text-[#8C7C6E]" data-id="element-387" data-mp="3kp2qwxtdwhqw">
                  {entry.who} · {entry.time}
                </span>
              </span>
              {/* Read and write are the only distinction that matters to the
               person signing off, so it is the one thing carrying colour. */}
              <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] ${entry.scope === 'write' ? 'bg-[#EA580C]/12 text-[#C2410C]' : 'bg-[#2B211A]/[0.06] text-[#8C7C6E]'}`} data-id="element-388" data-mp="31r04mqs80yws">
                {entry.scope}
              </span>
            </motion.div>)}
        </AnimatePresence>
      </div>
    </div>;
}
const EXPORTS = ['CSV', 'JSON', 'Postgres', 'S3', 'Webhook'] as const;
function ExportChip({
  label
}: {
  label: string;
}) {
  return <span className="whitespace-nowrap rounded-full border border-[#E7E0D4] bg-[#FCFAF6] px-[7px] py-1 text-[11px] font-semibold text-[#2B211A] sm:px-2.5 sm:py-1.5 sm:text-[12.5px] md:px-3 md:text-[13px]" data-id="element-389" data-mp="pevrenwl91a3">
      {label}
    </span>;
}
/**
 * The model picker. It is SELECTABLE rather than a static screenshot, because
 * the claim — that no vendor is baked in — is only convincing if you can watch
 * the model change and nothing else about the product move with it.
 */
const MODELS = [{
  id: 'gpt-5.2',
  name: 'GPT-5.2',
  logo: 'chatgpt',
  vendor: 'OpenAI',
  blurb: "OpenAI's flagship. Strong general reasoning with the broadest tool support, and the fastest of the frontier models on everyday work.",
  host: 'Hosted in the US by OpenAI'
}, {
  id: 'gpt-5.2-thinking',
  name: 'GPT-5.2 Thinking',
  logo: 'chatgpt',
  vendor: 'OpenAI',
  blurb: 'Extended reasoning for multi-step analysis. Slower per answer, materially better on work that has to be right the first time.',
  host: 'Hosted in the US by OpenAI'
}, {
  id: 'opus-4.5',
  name: 'Opus 4.5',
  logo: 'claude',
  vendor: 'Anthropic',
  blurb: "Anthropic's largest model. Best on long documents, careful writing, and following a brief closely across many steps.",
  host: 'Hosted in the EU by AWS'
}, {
  id: 'sonnet-4.5',
  name: 'Sonnet 4.5',
  logo: 'claude',
  vendor: 'Anthropic',
  blurb: "Anthropic's flagship model for complex tasks. Excels at coding, creative writing, and complex problem-solving tasks.",
  host: 'Hosted in the EU by AWS'
}, {
  id: 'gemini-3-pro',
  name: 'Gemini 3 Pro',
  logo: 'gemini',
  vendor: 'Google',
  blurb: "Google's flagship. The largest context window of the set, and the strongest on image and spreadsheet analysis.",
  host: 'Hosted in the EU by Google Cloud'
}, {
  id: 'gemini-2.5-pro',
  name: 'Gemini 2.5 Pro',
  logo: 'gemini',
  vendor: 'Google',
  blurb: 'The previous generation, kept available for teams that have already validated their workflows against it.',
  host: 'Hosted in the EU by Google Cloud'
}] as const;
const CAPABILITIES = [{
  label: 'Documents',
  Icon: FileText
}, {
  label: 'Web search',
  Icon: Globe
}, {
  label: 'Image analysis',
  Icon: ImageIcon
}, {
  label: 'Data analyst',
  Icon: Code2
}, {
  label: 'Thinking',
  Icon: Sparkles
}, {
  label: 'Canvas',
  Icon: PenLine
}] as const;
function ModelPicker() {
  const [active, setActive] = React.useState<string>('sonnet-4.5');
  const model = MODELS.find(m => m.id === active) ?? MODELS[0];
  return <div className="flex flex-col gap-3" data-id="element-390" data-mp="3h1u5lna88j93">
      {/* The trigger reads as the current selection, so changing a row in the
       list visibly changes what the product is running on. */}
      <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#E7E0D4] bg-white px-3.5 py-2 shadow-[0_2px_8px_rgba(43,33,26,0.06)]" data-id="element-391" data-mp="32wk1u0jb1nce">
        <BrandLogo slug={model.logo} className="size-[15px]" data-id="element-392" data-mp="2t0gzouogdopg" />
        <span className="text-[13px] font-bold tracking-[-0.01em] text-[#2B211A]" data-id="element-393" data-mp="2sxujk6nhy0vm">
          {model.name}
        </span>
        <ChevronDown aria-hidden="true" className="size-[13px] text-[#A99A8C]" data-id="element-394" data-mp="3lomivczuz5n6" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2" data-id="element-395" data-mp="3qnpfc91rw3jj">
        <div className="overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_8px_28px_-20px_rgba(43,33,26,0.5)]" data-id="element-396" data-mp="3qmeeagzpp9w4">
          <p className="px-3.5 pb-1.5 pt-3 text-[11.5px] font-semibold uppercase tracking-[0.1em] text-[#A99A8C]" data-id="element-397" data-mp="3bsjc1tu2aitw">
            Models
          </p>
          <ul data-id="element-398" data-mp="20b08u1ghefvc">
            {MODELS.map(m => {
            const on = m.id === model.id;
            return <li key={m.id} data-id="element-399" data-mp="25amn8e81q25q">
                  <button type="button" onClick={() => setActive(m.id)} aria-pressed={on} className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left transition-colors duration-150 ${on ? 'bg-[#FFF4EC]' : 'hover:bg-[#FCFAF6]'}`} data-id="element-400" data-mp="25bxp28hxkzu7">
                    <BrandLogo slug={m.logo} className="size-[15px] shrink-0" data-id="element-401" data-mp="21uf8s4ua0ny" />
                    <span className={`min-w-0 flex-1 truncate text-[13px] ${on ? 'font-bold text-[#2B211A]' : 'font-medium text-[#6B5D52]'}`} data-id="element-402" data-mp="1zrjdrh09dbb">
                      {m.name}
                    </span>
                    {on && <span aria-hidden="true" className="size-[6px] shrink-0 rounded-full bg-[#EA580C]" data-id="element-403" data-mp="3o6prqm8gqcs5" />}
                  </button>
                </li>;
          })}
          </ul>
        </div>

        {/* HIDDEN ON PHONES. The panel's claim is that no vendor is baked in,
         and the LIST proves that on its own — you change the row, the trigger
         changes with it. The detail card is corroboration, and on a phone it
         is a full extra screen of specs at the end of an already long board.
         Its content is not lost to assistive tech: the list is the control,
         and this only ever described the current selection. */}
        <div className="hidden rounded-2xl border border-[#EFEAE1] bg-white p-4 shadow-[0_8px_28px_-20px_rgba(43,33,26,0.5)] sm:block" data-id="element-404" data-mp="1kzgyunf6egak">
          <p className="flex items-center gap-2 text-[13px] text-[#6B5D52]" data-id="element-405" data-mp="1ptbqcd10vzk7">
            <BrandLogo slug={model.logo} className="size-[15px] shrink-0" data-id="element-406" data-mp="1upsev8c4wfxt" />
            <span data-id="element-407" data-mp="x3ct3ucocw4g">
              {model.vendor} <span className="text-[#CFC4B7]" data-id="element-408" data-mp="x4e6i5h5xupc">/</span>{' '}
              <span className="font-bold text-[#2B211A]" data-id="element-409" data-mp="120uy59nkmr76">{model.name}</span>
            </span>
          </p>
          <p className="mt-2.5 text-[12.5px] leading-relaxed text-[#6B5D52]" data-id="element-410" data-mp="16zpn6tc4tbzq">
            {model.blurb}
          </p>
          <p className="mt-4 text-[11.5px] font-semibold uppercase tracking-[0.1em] text-[#A99A8C]" data-id="element-411" data-mp="1c1f2il6e0btm">
            Capabilities
          </p>
          <ul className="mt-2 grid grid-cols-2 overflow-hidden rounded-xl border border-[#EFEAE1]" data-id="element-412" data-mp="2nez9vxcdqneb">
            {CAPABILITIES.map(({
            label,
            Icon
          }, i) => <li key={label} className={`flex items-center gap-2 px-2.5 py-2 text-[12px] text-[#2B211A] ${i % 2 === 0 ? 'border-r border-[#EFEAE1]' : ''} ${i < 4 ? 'border-b border-[#EFEAE1]' : ''}`} data-id="element-413" data-mp="2l2f0hpgkpwd5" data-mpi={i}>
                <Icon aria-hidden="true" className="size-[13px] shrink-0 text-[#8C7C6E]" data-id="element-414" data-mp="2uwfdedi3k5wf" />
                <span className="truncate" data-id="element-415" data-mp="2py45w9f9y7hd">{label}</span>
              </li>)}
          </ul>
          <p className="mt-3 text-[11.5px] text-[#A99A8C]" data-id="element-416" data-mp="33xn7ejdkykkp">{model.host}</p>
        </div>
      </div>
    </div>;
}
/**
 * THE SEVEN PANEL BODIES, each a component rather than inline JSX.
 *
 * They are lifted out so the same markup can be rendered twice: once inside
 * the animated board below, and once standalone on the export surface. Copying
 * the markup into an export page instead would mean two versions of every
 * panel drifting apart the moment either is edited.
 */
function OffloadCard() {
  // Same padding and the same copy-block height as its neighbour, so the two
  // visuals in this row start on one line. It used to run p-9 against the
  // neighbour's p-7, which put the two lists 8px plus a line of copy out of
  // register.
  return <div className="flex h-full flex-col p-5 sm:p-7" data-id="element-417" data-mp="3sbyoczarjt2a">
      <PanelCopy title="Offload the busywork and the associate work" copy="Hand Giga the research, formatting, follow-up, and first drafts. Your team keeps the judgment calls." className={ROW_COPY} data-id="element-418" data-mp="3sbgntqh2isok" />
      {/* The four jobs the copy names, each shown handed over and closed out.
       Naming them is the point: "busywork" is abstract until you can see which
       four things left your desk. */}
      <ul className="mt-6 rounded-2xl border border-[#F4DCCB] bg-white/70 p-2.5" data-id="element-419" data-mp="39n1v8yr3r6ln">
        {HANDOFF.map(item => <li key={item.label} className="flex items-center gap-3 border-b border-[#F4DCCB] px-3 py-3" data-id="element-420" data-mp="3ekjzibu6cxnb">
            {item.state === 'done' ? <span aria-hidden="true" className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[#EA580C]" data-id="element-421" data-mp="34luon30p4dgk">
                <svg viewBox="0 0 24 24" className="size-[12px]" fill="none" stroke="#fff" strokeWidth={3.4} strokeLinecap="round" strokeLinejoin="round" data-id="element-422" data-mp="2likg2ud0aq4h">
                  <path d="M4 12.5 9.5 18 20 6.5" data-id="element-423" data-mp="2brphqrmhk02o" />
                </svg>
              </span> : <span aria-hidden="true" className="relative flex size-[22px] shrink-0 items-center justify-center" data-id="element-424" data-mp="2bplgyijvvh6m">
                <svg viewBox="0 0 24 24" className="size-full" fill="none" data-id="element-425" data-mp="34gd8v3tx1jo5">
                  <circle cx="12" cy="12" r="10.5" stroke="#EA580C" strokeOpacity={0.2} strokeWidth={3} data-id="element-426" data-mp="34fllggah2s8y" />
                  <circle cx="12" cy="12" r="10.5" stroke="#EA580C" strokeWidth={3} strokeLinecap="round" strokeDasharray="46 100" data-id="element-427" data-mp="2zf7dey8vfiax" />
                </svg>
              </span>}
            <span className="min-w-0 flex-1" data-id="element-428" data-mp="2pib62vupl27y">
              <span className="block text-[13.5px] font-bold leading-tight text-[#2B211A]" data-id="element-429" data-mp="1j30qdkb8f944">
                {item.label}
              </span>
              <span className="mt-0.5 block truncate text-[12.5px] leading-snug text-[#6B5D52]" data-id="element-430" data-mp="1e35z3fnjohij">
                {item.detail}
              </span>
            </span>
            <span className="shrink-0 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#A99A8C]" data-id="element-431" data-mp="3uq46opg81rsr">
              {item.state === 'done' ? 'Done' : 'Drafting'}
            </span>
          </li>)}
        {/* THE LAST STEP OF THE HANDOFF, NOT A CAPTION. As a sentence below the
         list this read as a footnote on the panel; as the closing row it is
         where the ledger ends — the work goes out and the decision comes back.
         Deliberately not a checked row: it is the one line that is the
         reader's, so it points the other way and carries no status tag. */}
        <li className="flex items-center gap-3 px-3 py-3" data-id="element-432" data-mp="r9nbus10zhcp">
          <span aria-hidden="true" className="flex size-[22px] shrink-0 items-center justify-center rounded-full border border-[#EA580C]/35 bg-white" data-id="element-433" data-mp="r959ddnrs6wc">
            <svg viewBox="0 0 24 24" className="size-[12px]" fill="none" stroke="#EA580C" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" data-id="element-434" data-mp="2t0s67vucew0q">
              <path d="M10 5 3.5 11.5 10 18M3.5 11.5H20" data-id="element-435" data-mp="yrd7p2f9fj3o" />
            </svg>
          </span>
          <span className="text-[13.5px] font-bold leading-tight text-[#2B211A]" data-id="element-436" data-mp="ttjzk3uecf0r">
            Back to you for the judgment calls
          </span>
        </li>
      </ul>
    </div>;
}
function MeetingsCard() {
  return <div className="flex h-full flex-col p-5 sm:p-7" data-id="element-437" data-mp="13kocky0ihpsy">
      <PanelCopy title="Kill the status meeting" copy="Scattered conversations become updates, decisions, and next steps — with nothing new on the calendar." className={ROW_COPY} data-id="element-438" data-mp="ftiqi13za1a7" />
      <KilledMeetings data-id="element-439" data-mp="ps8b41n9dzu5" />
    </div>;
}
function AnswersCard() {
  return <div className="p-5 sm:p-7" data-id="element-440" data-mp="pqf1cdhcmzhx">
      <PanelCopy title="Get real-time answers" copy="Ask in plain language where your team already works. Answers come from the live systems." data-id="element-441" data-mp="ktonlwt0ofzo" />
      <AskThread data-id="element-442" data-mp="2163ob4ulryiq" />
    </div>;
}
function OwnDataCard() {
  return <div className="flex h-full flex-col p-5 sm:p-7" data-id="element-443" data-mp="1w93hustuctr3">
      <PanelCopy title="Own your own data" copy="Export everything, keep your history portable, and switch systems without losing the work behind you." data-id="element-444" data-mp="1waocsjk2ieph" />
      {/* A dotted volume of data with its lower-right corner dissolving into
       loose points — the body of work leaving as portable pieces, and the chips
       below are the formats it comes apart into. At full strength: the artwork
       is halftone dots on transparent, so any opacity or blend only muddies it.
       Scaled with a transform rather than a width, and anchored on the
       artwork's own centre of mass (51% / 35%) rather than the file's
       geometric centre — the source carries very wide empty margins, so sizing
       by width was really sizing the whitespace. A FIXED band, not a growing
       one: as flex-1 this panel expanded to whatever height it could take and
       set the height of the whole row. */}
      <div className="mt-5 flex h-[136px] items-center justify-center md:mt-6 md:h-[188px]" data-id="element-445" data-mp="35fc3ikrvkhmo">
        <img src="https://cdn.magicpatterns.com/uploads/7WXYe7pZJQQgXizhLbkmbK/dotcube-transparent.png" alt="" aria-hidden="true" className="pointer-events-none w-full max-w-none select-none scale-[0.92] object-contain md:scale-[1.4]" style={{
        transformOrigin: '51% 35%'
      }} data-id="element-446" data-mp="3a9gdlcwft5rz" />
      </div>
      {/* ONE ROW AT EVERY WIDTH. The five formats are a set — what the cube
       comes apart INTO — so a second line made the fifth read as an
       afterthought rather than a member. */}
      <div className="mt-auto flex flex-nowrap items-center justify-center gap-1 pt-6 sm:gap-1.5 md:justify-start md:gap-2 md:pt-8" data-id="element-447" data-mp="3ss474wggk3ik">
        {EXPORTS.map(format => <ExportChip key={format} label={format} data-id="element-448" data-mp="5l23cf5p5irj" />)}
      </div>
    </div>;
}
function ActivityCard() {
  return <div className="flex h-full flex-col p-5 sm:p-7" data-id="element-449" data-mp="aky8ihq4rg8o">
      <PanelCopy title="See it as it happens" copy="Every action Giga takes is logged with who asked, which system it touched, and whether anything was written." data-id="element-450" data-mp="aibsdtp6bseu" />
      <LiveActivity data-id="element-451" data-mp="1lyjw3ygcd9s3" />
    </div>;
}
function TeamsCard() {
  return <div className="flex h-full flex-col p-5 sm:p-7" data-id="element-452" data-mp="36ifbdl3suqtr">
      <PanelCopy title="Built for teams" copy="Shared access, workflows, and company context in one place — not a drawer of personal accounts." className={ROW_COPY_BOTTOM} data-id="element-453" data-mp="36h45nfqsn900" />
      {/* The group list said "teams" as a table of scopes; the figure says it as
       a group of people. In a board where every other panel is a piece of UI,
       an illustration here also gives the eye somewhere to rest — and it is the
       one claim about PEOPLE rather than plumbing.
       ONE CUT AT EVERY WIDTH: this file is transparent-ground and close-
       cropped, so it sits inside the card's padding at its natural proportions
       on every screen — no fixed height, no clipping container, no overscale,
       nothing cut. */}
      {/* BIGGER ON DESKTOP BY REMOVING THE BOX, NOT BY OVERSCALING. The image
       was width-constrained by the card's own p-7, so the only headroom left
       was the padding itself: negative margins on md up cancel it on three
       sides and the artwork runs edge to edge and sits on the card's bottom
       lip. Scaling past that would push the outer figures into the card's
       overflow clip, which is what cropped them in every earlier attempt. */}
      <div className="mt-auto flex justify-center px-2 pb-1 pt-4 md:-mx-7 md:-mb-7 md:px-0 md:pb-0 md:pt-8" data-id="element-454" data-mp="1rme336qcmhf8">
        <img src="https://cdn.magicpatterns.com/uploads/j2b2syUqR6iQwjNH3HkLqj/Untitled_(YouTube_Thumbnail)_(1).png" alt="" aria-hidden="true" className="mx-auto block h-auto w-full max-w-none select-none object-contain" style={{
        // Warmed a touch: the source is cold neutral grey, which reads as a
        // foreign asset dropped onto a warm cream page.
        filter: 'sepia(0.16) saturate(1.05) brightness(1.01)'
      }} data-id="element-455" data-mp="1wk5snu3itrg9" />
      </div>
    </div>;
}
function ModelCard() {
  return <div className="p-5 sm:p-7" data-id="element-456" data-mp="zs29yzyvoxoy">
      <PanelCopy title="Model agnostic" copy="Every leading model, no lock-in. Switch whenever a better one ships — your context, connections, and history stay in Giga." className={ROW_COPY_BOTTOM} data-id="element-457" data-mp="13v8muqghxn4e" />
      <div className="mt-6" data-id="element-458" data-mp="rcmski0oirfe">
        <ModelPicker data-id="element-459" data-mp="rdecgvkwz0pu" />
      </div>
    </div>;
}

/**
 * EXPORT SURFACE. The same seven panels, in the same card frame, but static —
 * no scroll trigger, no rise, no stagger — so each one can be vectorised on
 * its own at a fixed width. `Panel` animates on entry into view, and an
 * off-screen export frame never enters view, so exporting the animated version
 * would capture a card at opacity 0.
 */
function StaticPanel({
  children,
  tint = 'white'
}: {
  children: React.ReactNode;
  tint?: 'white' | 'peach' | 'sand';
}) {
  const surface = tint === 'peach' ? 'bg-[#FFF4EC]' : tint === 'sand' ? 'bg-[#F7F2EA]' : 'bg-white';
  return <article className={`relative flex w-full flex-col overflow-hidden rounded-[22px] border border-[#E7E0D4] ${surface} shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)]`} data-id="element-460" data-mp="iizz4yjy209d">
      {children}
    </article>;
}
export const MOSAIC_CARDS: Array<{
  id: string;
  name: string;
  note: string;
  render: () => React.ReactNode;
}> = [{
  id: 'mosaic-offload',
  name: 'Offload the busywork',
  note: 'Four jobs handed over, judgment calls returned',
  render: () => <StaticPanel tint="peach" data-id="element-461" data-mp="2y9jb85pw7vfz">
        <OffloadCard data-id="element-462" data-mp="2y889scjx6zs1" />
      </StaticPanel>
}, {
  id: 'mosaic-meetings',
  name: 'Kill the status meeting',
  note: 'Meetings struck through, recap posted',
  render: () => <StaticPanel data-id="element-463" data-mp="37zcln3eau4in">
        <MeetingsCard data-id="element-464" data-mp="37zumyeftj8xf" />
      </StaticPanel>
}, {
  id: 'mosaic-answers',
  name: 'Get real-time answers',
  note: 'Asked in the channel, answered from live systems',
  render: () => <StaticPanel tint="sand" data-id="element-465" data-mp="3h23y96iwv6bv">
        <AnswersCard data-id="element-466" data-mp="3gypze89ghf96" />
      </StaticPanel>
}, {
  id: 'mosaic-own-data',
  name: 'Own your own data',
  note: 'Dot cube dissolving into export formats',
  render: () => <StaticPanel data-id="element-467" data-mp="1mo31qsau2vey">
        <OwnDataCard data-id="element-468" data-mp="1mom95f2uy1eq" />
      </StaticPanel>
}, {
  id: 'mosaic-activity',
  name: 'See it as it happens',
  note: 'Action log with who asked and what was touched',
  render: () => <StaticPanel data-id="element-469" data-mp="1k3erev5ufacr">
        <ActivityCard data-id="element-470" data-mp="1k3y3hv8tb4iv" />
      </StaticPanel>
}, {
  id: 'mosaic-teams',
  name: 'Built for teams',
  note: 'The group illustration',
  render: () => <StaticPanel data-id="element-471" data-mp="1acaf637jy3lm">
        <TeamsCard data-id="element-472" data-mp="1absaqjaqkj2m" />
      </StaticPanel>
}, {
  id: 'mosaic-model',
  name: 'Model agnostic',
  note: 'Provider picker, no lock-in',
  render: () => <StaticPanel tint="sand" data-id="element-473" data-mp="15fa6rln8corc">
        <ModelCard data-id="element-474" data-mp="2317npelyom16" />
      </StaticPanel>
}];
/**
 * The sentence and its full stop are held separately so the stop can be set in
 * the accent colour. Across the site the terminating dot is always the
 * opposite of the words it ends — orange on ink type, and the reverse on the
 * one headline whose closing phrase is already orange. It is the smallest
 * possible mark, which is exactly why it can carry the accent without
 * competing with anything.
 */
const MOSAIC_BODY = 'The fastest version of your company is already possible';
const MOSAIC_HEADLINE = `${MOSAIC_BODY}.`;

/**
 * The claim writes itself out under an orange caret, the same way the Company
 * Brain console streams its answer.
 *
 * It used to be phone-only, on the reasoning that a headline typing itself
 * beside live product panels would read as theatre. In practice the opposite
 * is true: the board below is a set of seven quiet, static panels, and without
 * this the section simply appears fully formed with nothing directing the eye
 * to where it starts. The caret is what makes the reader begin at the sentence
 * rather than at whichever panel happens to catch them. It also ties the
 * section to the console, which is the page's one other piece of typed-out
 * text.
 *
 * The full sentence is always in the DOM at zero opacity as a SIZER, with the
 * growing slice laid over it — otherwise the block would grow line by line and
 * shove the board down the page as it typed.
 */
function TypedMosaicHeadline() {
  const reduce = useReducedMotion();
  const ref = React.useRef<HTMLHeadingElement>(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.5
  });
  const animate = !reduce;
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!animate || !inView) return;
    if (count >= MOSAIC_HEADLINE.length) return;
    // Two characters at a time on most ticks: at one per tick a 56-character
    // sentence takes over two seconds, which is long enough that the reader
    // starts waiting on it instead of reading it. The uneven 1-or-2 step is
    // what keeps it from reading as a machine ticking at a fixed rate.
    const t = window.setTimeout(() => setCount(c => Math.min(MOSAIC_HEADLINE.length, c + (Math.random() < 0.4 ? 1 : 2))), 26);
    return () => window.clearTimeout(t);
  }, [animate, inView, count]);
  const done = !animate || count >= MOSAIC_HEADLINE.length;
  return <h2 ref={ref} className="font-title relative text-[32px] font-bold leading-[1.08] tracking-tight text-[#2B211A] sm:text-4xl md:text-[56px] md:leading-[1.14]" data-id="element-475" data-mp="g85s9mwk83e">
      <span aria-hidden={!done} className={done ? undefined : 'invisible'} data-id="element-476" data-mp="3rl7vnkw50ytl">
        {MOSAIC_BODY}
        <span className="text-[#EA580C]" data-id="element-477" data-mp="12wgk0ihm0q39">.</span>
      </span>
      {!done && <span aria-hidden="true" className="absolute inset-0" data-id="element-478" data-mp="16u4h9s6ryuyb">
          {MOSAIC_HEADLINE.slice(0, Math.min(count, MOSAIC_BODY.length))}
          {count > MOSAIC_BODY.length && <span className="text-[#EA580C]" data-id="element-479" data-mp="1gu54hp7rlv7s">.</span>}
          <motion.span className="ml-1 inline-block h-[0.72em] w-[0.4em] translate-y-[0.02em] rounded-[1px] bg-[#EA580C]" animate={{
        opacity: [1, 0.2, 1]
      }} transition={{
        duration: 0.7,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-480" data-mp="1grso3ghnnswu" />
        </span>}
    </h2>;
}
export function AccelerationMosaic() {
  return <div data-id="element-481" data-mp="xvj4kdnzia5g">
      <SectionIntro align="left" className="max-w-[720px]" data-id="element-482" data-mp="xvansaoqqkoz">
        <TypedMosaicHeadline data-id="element-483" data-mp="xwu421aecvqr" />
        {/* The lead now DEFINES THE CATEGORY rather than pre-empting an
         objection. "AI platform" is the term the hero opens on and the term
         the whole page rests on, and nowhere did the page say what one is —
         so a reader arriving cold had to infer it from the board below.
         Independence is still the closing clause, but it belongs at the end
         of the definition, not in place of it. */}
        <p className="mt-4 max-w-[54ch] text-[16.5px] leading-relaxed text-[#6B5D52] md:mt-5 md:text-lg" data-id="element-484" data-mp="3k0udi1ruct8p">
          Simple for business users. Ready for advanced use-cases.
        </p>
      </SectionIntro>

      {/* One board, sized by importance rather than split into equal tiles:
       the lead panel is the one people came for, the rest support it. */}
      <div className="mt-12 grid grid-cols-1 gap-4 md:mt-16 md:grid-cols-12 md:gap-5" data-id="element-485" data-mp="31bo0mvdqavut">
        {/* LEAD — the work Giga takes off the team's hands. Wider than its
         neighbour but ONE row tall: spanning two rows made it tall enough to
         need filler, and a panel padded to fit the grid stops reading as the
         most important thing on the board. */}
        {/* DELAYS RESET PER ROW. They used to climb 0.04 -> 0.4 across the
         whole board, but each panel triggers on its OWN entry into view — so
         the last panel sat still for 400ms after it had already appeared,
         which reads as lag rather than as stagger. Numbered by position
         within its row, every row staggers relative to the moment you
         actually reach it, and nothing waits longer than 140ms. */}
        <Panel tint="peach" className="md:col-span-7" data-id="element-486" data-mp="132tt9d35jxj2">
          <OffloadCard data-id="element-487" data-mp="130hbazxe9n60" />
        </Panel>

        {/* Meetings collapsed into a posted recap. */}
        <Panel className="md:col-span-5" delay={0.07} data-id="element-488" data-mp="y1e69fhezgy1">
          <MeetingsCard data-id="element-489" data-mp="u0ubujo86rfl" />
        </Panel>

        {/* Numbers, without the wait. */}
        <Panel tint="sand" className="md:col-span-4" data-id="element-490" data-mp="p3c2is6aqa72">
          <AnswersCard data-id="element-491" data-mp="p4wss5lkv6z4" />
        </Panel>

        {/* CONTROL — data ownership. */}
        <Panel className="md:col-span-4" delay={0.07} data-id="element-492" data-mp="3etpbhzqiysy7">
          <OwnDataCard data-id="element-493" data-mp="39si5a54161tn" />
        </Panel>

        {/* CONTROL — what is happening, as it happens. */}
        <Panel className="md:col-span-4" delay={0.14} data-id="element-494" data-mp="1pbipib82jhij">
          <ActivityCard data-id="element-495" data-mp="1p8w9rob0xvp8" />
        </Panel>

        {/* CONTROL — one place, not a drawer of personal accounts. */}
        <Panel className="md:col-span-5" data-id="element-496" data-mp="1u7pqrqpis4u5">
          <TeamsCard data-id="element-497" data-mp="1yamaxlupk907" />
        </Panel>

        {/* CONTROL — the strongest independence claim, so it closes the board.
         Paired rather than run full width: the picker is already two columns
         internally, so a full-width panel gave it more room than it needed and
         left the copy stranded on one side. */}
        <Panel tint="sand" className="md:col-span-7" delay={0.07} data-id="element-498" data-mp="2cyqjvy3yl65s">
          <ModelCard data-id="element-499" data-mp="2hzehx3qlun9i" />
        </Panel>
      </div>
    </div>;
}